import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { Logo } from "../components/Logo";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const [status, setStatus] = useState("checking"); // checking | paid | failed
  const sessionId = params.get("session_id");

  useEffect(() => {
    if (!sessionId) { setStatus("failed"); return; }
    let attempts = 0;
    let cancelled = false;
    const poll = async () => {
      if (cancelled || attempts >= 8) {
        if (!cancelled) setStatus("failed");
        return;
      }
      attempts++;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          if (!cancelled) setStatus("paid");
          return;
        }
        if (data.status === "expired") {
          if (!cancelled) setStatus("failed");
          return;
        }
      } catch (e) { /* keep polling */ }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen grid place-items-center bg-slate-50 px-4">
      <div className="max-w-md w-full bg-white rounded-2xl border border-slate-200 p-8 text-center">
        <div className="flex justify-center mb-6"><Logo /></div>
        {status === "checking" && (
          <div data-testid="payment-checking">
            <Loader2 className="h-12 w-12 mx-auto text-blue-600 animate-spin" />
            <h1 className="mt-4 font-heading font-bold text-2xl text-slate-900">Confirming your payment…</h1>
            <p className="mt-2 text-slate-600 text-sm">This usually takes a few seconds.</p>
          </div>
        )}
        {status === "paid" && (
          <div data-testid="payment-success">
            <CheckCircle2 className="h-12 w-12 mx-auto text-emerald-500" />
            <h1 className="mt-4 font-heading font-bold text-2xl text-slate-900">Payment successful!</h1>
            <p className="mt-2 text-slate-600 text-sm">Your subscription is now active.</p>
            <Link to="/dashboard" className="mt-6 inline-block px-6 py-3 rounded-full bg-blue-600 text-white font-semibold hover:bg-blue-700">Go to dashboard</Link>
          </div>
        )}
        {status === "failed" && (
          <div data-testid="payment-failed">
            <XCircle className="h-12 w-12 mx-auto text-rose-500" />
            <h1 className="mt-4 font-heading font-bold text-2xl text-slate-900">Payment not confirmed</h1>
            <p className="mt-2 text-slate-600 text-sm">Please try again or contact support.</p>
            <Link to="/dashboard" className="mt-6 inline-block px-6 py-3 rounded-full bg-blue-600 text-white font-semibold hover:bg-blue-700">Go to dashboard</Link>
          </div>
        )}
      </div>
    </div>
  );
}
